import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

const Slider = ({ label, value, min, max, step, onChange, display, testid }) => {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <label className="text-[10px] font-bold uppercase tracking-[0.24em] text-wealth/50">{label}</label>
        <span className="font-display text-2xl font-medium text-wealth">{display}</span>
      </div>
      <input
        type="range"
        data-testid={testid}
        aria-label={label}
        className="calc-range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--fill": `${fill}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
};

export const Calculator = () => {
  const [mode, setMode] = useState("sip");
  const [amount, setAmount] = useState(10000);
  const [years, setYears] = useState(12);
  const [rate, setRate] = useState(12);

  const { future, invested, gains } = useMemo(() => {
    const r = rate / 100;
    if (mode === "sip") {
      const i = r / 12;
      const n = years * 12;
      const fv = amount * (((Math.pow(1 + i, n) - 1) / i) * (1 + i));
      const inv = amount * n;
      return { future: fv, invested: inv, gains: fv - inv };
    }
    const fv = amount * Math.pow(1 + r, years);
    return { future: fv, invested: amount, gains: fv - amount };
  }, [mode, amount, years, rate]);

  const gainPct = Math.min(100, (gains / future) * 100);

  return (
    <section id="calculator" data-testid="calculator-section" className="bg-bone py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <Reveal>
            <Eyebrow index="04" label="SIP & Lumpsum calculator" />
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-wealth mt-5">
              See what patience <span className="italic text-wealth-light">is worth.</span>
            </h2>

            <div className="mt-10 inline-flex border border-wealth/20 rounded-sm p-1">
              {["sip", "lumpsum"].map((m) => (
                <button
                  key={m}
                  data-testid={`calc-mode-${m}`}
                  onClick={() => {
                    setMode(m);
                    setAmount(m === "sip" ? 10000 : 100000);
                  }}
                  className={`px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] rounded-sm transition-[background-color,color] duration-300 ${
                    mode === m ? "bg-wealth text-bone" : "text-wealth/60 hover:text-wealth"
                  }`}
                >
                  {m === "sip" ? "SIP" : "Lumpsum"}
                </button>
              ))}
            </div>

            <div className="mt-12 space-y-12">
              <Slider
                label={mode === "sip" ? "Monthly investment" : "Lumpsum amount"}
                value={amount}
                min={mode === "sip" ? 500 : 10000}
                max={mode === "sip" ? 100000 : 1000000}
                step={mode === "sip" ? 500 : 5000}
                onChange={setAmount}
                display={inr.format(amount)}
                testid="calc-amount-slider"
              />
              <Slider
                label="Investment period"
                value={years}
                min={1}
                max={30}
                step={1}
                onChange={setYears}
                display={`${years} yrs`}
                testid="calc-years-slider"
              />
              <Slider
                label="Expected return (p.a.)"
                value={rate}
                min={6}
                max={18}
                step={0.5}
                onChange={setRate}
                display={`${rate}%`}
                testid="calc-rate-slider"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 flex items-center">
          <Reveal delay={0.15} className="w-full">
            <div className="grain relative bg-wealth-dark rounded-sm p-10 md:p-14 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(103,37,244,0.16),transparent_55%)]" />
              <div className="relative z-10">
                <p className="text-[10px] font-bold uppercase tracking-[0.26em] text-bone/50">
                  Potential value after {years} years
                </p>
                <motion.p
                  key={Math.round(future)}
                  data-testid="calc-result-value"
                  initial={{ opacity: 0.4, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="font-display text-6xl md:text-7xl font-light text-brand-light mt-4 tracking-tight"
                >
                  {inr.format(Math.round(future))}
                </motion.p>

                <div className="mt-10 space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-bone/55">Invested amount</span>
                    <span data-testid="calc-invested-value" className="text-bone font-medium">{inr.format(Math.round(invested))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-bone/55">Estimated gains</span>
                    <span data-testid="calc-gains-value" className="text-brand-light font-medium">{inr.format(Math.round(gains))}</span>
                  </div>
                  <div className="h-1.5 bg-bone/15 rounded-full overflow-hidden flex">
                    <div className="bg-bone/50 h-full" style={{ width: `${100 - gainPct}%` }} />
                    <div className="bg-brand h-full transition-[width] duration-500" style={{ width: `${gainPct}%` }} />
                  </div>
                </div>

                <p className="mt-10 text-[11px] leading-relaxed text-bone/40">
                  Illustration only, at {rate}% p.a. assumed returns. Mutual fund investments are subject to market risks.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
